import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { ExpandMore, ExpandLess, Summarize } from '@mui/icons-material'
import { MONTHS } from './GridConfig'
import { formatNumber } from '../../utils/format'

export default function LineSummary({ rowData = [] }) {
  const [expanded, setExpanded] = useState(true)

  const lineTotals = useMemo(() => {
    const lines = {}
    rowData.forEach((row) => {
      const line = row.line || 'Unknown'
      if (!lines[line]) {
        lines[line] = { line, months: {} }
        MONTHS.forEach((m) => {
          lines[line].months[m.key] = { cases: 0, hrs: 0 }
        })
      }
      MONTHS.forEach((m) => {
        lines[line].months[m.key].cases += row[`${m.key}_cases`] || 0
        lines[line].months[m.key].hrs += row[`${m.key}_hrs`] || 0
      })
    })
    return Object.values(lines).sort((a, b) => String(a.line).localeCompare(String(b.line)))
  }, [rowData])

  const startingInventory = useMemo(() => {
    const inv = {}
    MONTHS.forEach((m) => {
      inv[m.key] = 0
    })
    rowData.forEach((row) => {
      MONTHS.forEach((m, i) => {
        if (i === 0 && row.beginningInv != null) {
          inv[m.key] += row.beginningInv
        }
      })
    })
    return inv
  }, [rowData])

  const grandTotal = useMemo(() => {
    const totals = {}
    MONTHS.forEach((m) => {
      totals[m.key] = { cases: 0, hrs: 0 }
    })
    lineTotals.forEach((lt) => {
      MONTHS.forEach((m) => {
        totals[m.key].cases += lt.months[m.key].cases
        totals[m.key].hrs += lt.months[m.key].hrs
      })
    })
    return totals
  }, [lineTotals])

  const cellSx = { fontSize: '0.7rem', py: 0.25, px: 0.75 }
  const headerSx = { ...cellSx, fontWeight: 700 }
  const MAX_HRS = 160

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      backgroundColor: 'background.paper',
      overflow: 'hidden',
    }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Summarize sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Line Summary
        </Typography>
        <Tooltip title={expanded ? 'Collapse Line Summary' : 'Expand Line Summary'}>
          <IconButton size="small">
            {expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <TableContainer sx={{ maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerSx, width: 100 }}>Metric</TableCell>
                {MONTHS.map((m) => (
                  <TableCell key={m.key} align="right" sx={headerSx}>
                    {m.label.split(' ')[0]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Starting Inv</TableCell>
                {MONTHS.map((m) => (
                  <TableCell key={m.key} align="right" sx={cellSx}>
                    {formatNumber(startingInventory[m.key])}
                  </TableCell>
                ))}
              </TableRow>

              {lineTotals.map((lt) => (
                <TableRow key={`cases-${lt.line}`}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    Line {lt.line} Cases
                  </TableCell>
                  {MONTHS.map((m) => (
                    <TableCell key={m.key} align="right" sx={cellSx}>
                      {formatNumber(lt.months[m.key].cases)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {lineTotals.map((lt) => (
                <TableRow key={`hrs-${lt.line}`}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    Line {lt.line} Hrs
                  </TableCell>
                  {MONTHS.map((m) => (
                    <TableCell
                      key={m.key}
                      align="right"
                      sx={{
                        ...cellSx,
                        fontWeight: lt.months[m.key].hrs > MAX_HRS ? 700 : 400,
                        color: lt.months[m.key].hrs > MAX_HRS ? 'error.main' : 'text.primary',
                      }}
                    >
                      {lt.months[m.key].hrs.toFixed(1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider' } }}>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Total Cases</TableCell>
                {MONTHS.map((m) => (
                  <TableCell key={m.key} align="right" sx={{ ...cellSx, fontWeight: 700 }}>
                    {formatNumber(grandTotal[m.key].cases)}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Total Hrs</TableCell>
                {MONTHS.map((m) => (
                  <TableCell key={m.key} align="right" sx={{ ...cellSx, fontWeight: 700 }}>
                    {grandTotal[m.key].hrs.toFixed(1)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  )
}
